import { useEffect, useState } from 'react';
import { Bell, CalendarDays, Clock, Download, ListChecks, ScanFace, User, Users } from 'lucide-react';
import { api } from '../../api';
import Ayuda from '../../ui/Ayuda';
import { Cargando, ErrorConReintento } from '../../ui/EstadoDePantalla';
import { useSubpantalla } from '../../ui/navegacionReact';
import { useAuth } from '../../lib/contextoAuth';
import { Avisos, MisRegistros, Revision, Rostro } from './AsistenciaRostro';

/* =====================================================================
   Asistencia · DISEÑO-ASISTENCIA §8 (módulo del carril terminal)
   =====================================================================
   Hoy · Semana · Horas · Horarios. Todo lo calcula el servidor con la hora
   de la empresa; aquí solo se pinta. Ámbar = retardo, fuera de orden o sin
   validar; azul = campo o comida. Cada pestaña dice que no es nómina.
   ===================================================================== */
const PESTANAS = [
  { id: 'hoy', titulo: 'Hoy', icono: Users },
  { id: 'semana', titulo: 'Semana', icono: CalendarDays },
  { id: 'horas', titulo: 'Horas', icono: Clock },
  { id: 'horarios', titulo: 'Horarios', icono: Clock },
  // TP4-R (DIRECTRIZ-TERMINAL-ROSTRO §6–§8): solo se ven con su permiso.
  { id: 'revision', titulo: 'Revisión', icono: ListChecks, permiso: 'asistencia.revisar' },
  { id: 'avisos', titulo: 'Avisos', icono: Bell, permiso: 'asistencia.revisar' },
  { id: 'rostro', titulo: 'Rostro', icono: ScanFace, permiso: 'presencia.ver' },
  { id: 'mios', titulo: 'Mis registros', icono: User, permiso: 'asistencia.marcar' },
];
const ESTADO = { DENTRO: 'Dentro', COMIENDO: 'Comiendo', EN_CAMPO: 'En campo', FUERA: 'Fuera' };
const CLAS = {
  A_TIEMPO: 'a tiempo', RETARDO: 'retardo', ANTICIPADO: 'antes', SALIDA_ANTICIPADA: 'salió antes',
  TIEMPO_EXTRA: 'tiempo extra', FUERA_DE_SECUENCIA: 'fuera de orden', SIN_HORARIO: 'sin horario',
};
const DIAS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const NO_NOMINA = 'Esto es control de asistencia: dice quién marcó y a qué hora. TEGO no calcula nómina.';

function useDatos(ruta) {
  // La respuesta se guarda con la clave de la petición que la trajo: así
  // «cargando» se deduce (la clave vigente aún no tiene respuesta) sin
  // poner estado dentro del efecto.
  const [n, setN] = useState(0);
  const clave = `${ruta}#${n}`;
  const [r, setR] = useState({ clave: null, d: null, error: null });
  useEffect(() => {
    let vivo = true;
    api.get(ruta)
      .then((x) => vivo && setR({ clave, d: x, error: null }))
      .catch((e) => vivo && setR({ clave, d: null, error: e }));
    return () => { vivo = false; };
  }, [ruta, n, clave]);
  const vigente = r.clave === clave;
  return { d: vigente ? r.d : null, error: vigente ? r.error : null, recargar: () => setN((x) => x + 1) };
}

function Estado({ d, error, recargar, que, children }) {
  if (error) return <ErrorConReintento que={`No se pudo cargar ${que}`} porque={error.message} onReintentar={recargar} />;
  if (!d) return <Cargando filas={3} etiqueta={`Cargando ${que}`} />;
  return children;
}

function Contador({ titulo, valor, tono }) {
  return (
    <div className={`rounded-xl border p-3 ${tono}`}>
      <div className="text-2xl font-bold">{valor}</div>
      <div className="text-sm">{titulo}</div>
    </div>
  );
}

function Anular({ fila, evento, alTerminar }) {
  const [motivo, setMotivo] = useState('');
  const [msg, setMsg] = useState(null);
  const listo = motivo.trim().length >= 3;
  async function enviar() {
    try {
      const r = await api.post('/asistencia/ajustes', {
        id_usuario: fila.id_usuario, accion: 'ANULAR', ajusta_a: evento.id, motivo,
        uuid_cliente: globalThis.crypto?.randomUUID?.() || String(Date.now()) });
      setMsg(r.mensaje); alTerminar();
    } catch (e) { setMsg(`${e.message} ${e.queHacer || ''}`); }
  }
  return (
    <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3" role="dialog" aria-label="Anular registro">
      <p className="text-sm text-gray-700">Anular deja el registro de las {evento.hora} fuera del día; el original se conserva en el rastro.</p>
      <Ayuda id="terminal.asistencia.ajuste" />
      <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo (obligatorio)"
        className="min-h-12 w-full rounded-lg border px-3" />
      <button type="button" disabled={!listo} onClick={enviar}
        className="min-h-12 w-full rounded-lg bg-blue-600 font-semibold text-white disabled:bg-gray-300 disabled:text-gray-600">Anular registro</button>
      {!listo && <p className="text-sm text-gray-600">Escribe el motivo para poder anular.</p>}
      {msg && <p role="status" className="text-sm">{msg}</p>}
    </div>
  );
}

function Hoy() {
  const h = useDatos('/asistencia/hoy');
  const [abierto, setAbierto] = useState(null);
  // El formulario de anular es una subpantalla: atrás lo cierra, no sale de Asistencia.
  useSubpantalla('anular', !!abierto, () => setAbierto(null));
  return (
    <Estado {...h} que="la asistencia de hoy">
      {h.d && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Contador titulo="Dentro" valor={h.d.contadores.dentro} tono="border-gray-200 bg-white" />
            <Contador titulo="Comiendo o en campo" valor={h.d.contadores.comiendo + h.d.contadores.en_campo} tono="border-sky-200 bg-sky-50" />
            <Contador titulo="Retardos" valor={h.d.contadores.retardos} tono="border-amber-200 bg-amber-50" />
            <Contador titulo="Faltan" valor={h.d.contadores.faltan} tono="border-amber-200 bg-amber-50" />
          </div>
          {h.d.filas.length === 0 && (
            <div className="rounded-xl border border-dashed p-6 text-center text-gray-600">
              Hoy nadie ha pasado lista. Cuando alguien marque en la terminal o con su teléfono, aparece aquí.
            </div>
          )}
          <ul className="space-y-2">
            {h.d.filas.map((f) => {
              const ambar = f.retardo || f.fuera_de_secuencia || f.sin_validar || f.falta;
              return (
                <li key={f.id_usuario} className={`rounded-xl border p-3 ${ambar ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-semibold text-gray-900">{f.nombre}</span>
                    <span className="text-sm">{f.falta ? 'No ha llegado' : ESTADO[f.estado]}{f.desde ? ` · desde ${f.desde}` : ''}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-2 text-sm text-gray-700">
                    {f.eventos.map((e) => (
                      <button type="button" key={e.id} onClick={() => setAbierto({ f, e })}
                        className="min-h-10 rounded-full border border-gray-300 bg-white px-3">
                        {e.hora} {CLAS[e.clasificacion] || ''}{e.desfase_min ? ` (${e.desfase_min} min)` : ''}
                      </button>
                    ))}
                  </div>
                  {abierto?.f === f && <Anular fila={f} evento={abierto.e} alTerminar={() => { setAbierto(null); h.recargar(); }} />}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Estado>
  );
}

function Semana() {
  const s = useDatos('/asistencia/semana');
  return (
    <Estado {...s} que="la semana">
      {s.d && (
        <div className="space-y-3">
          <a href="/api/asistencia/semana?formato=csv" download
            className="flex min-h-12 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white font-semibold">
            <Download className="h-5 w-5" aria-hidden="true" /> Bajar a Excel
          </a>
          {s.d.personas.length === 0 && <p className="rounded-xl border border-dashed p-6 text-center text-gray-600">Esta semana no hay registros. Aparecen en cuanto alguien pase lista.</p>}
          <ul className="space-y-2">
            {s.d.personas.map((p) => (
              <li key={p.id_usuario} className="rounded-xl border border-gray-200 bg-white p-3 text-sm">
                <div className="font-semibold text-gray-900">{p.nombre}</div>
                <div>Retardos: {p.retardos} ({p.minutos_retardo} min) · Salidas antes: {p.salidas_anticipadas} · Tiempo extra: {p.tiempo_extra}</div>
                <div>Salidas a campo antes de la entrada: {p.anticipaciones_campo} · Fuera de orden: {p.fuera_de_secuencia} · Faltas: {p.faltas}</div>
              </li>
            ))}
          </ul>
          <Ayuda id="terminal.asistencia.campo" />
        </div>
      )}
    </Estado>
  );
}

function Horas() {
  const h = useDatos('/asistencia/horas');
  return (
    <Estado {...h} que="las horas">
      {h.d && (
        <div className="space-y-3">
          <a href="/api/asistencia/horas?formato=csv" download
            className="flex min-h-12 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white font-semibold">
            <Download className="h-5 w-5" aria-hidden="true" /> Bajar a Excel
          </a>
          {h.d.filas.length === 0 && <p className="rounded-xl border border-dashed p-6 text-center text-gray-600">No hay horas que sumar en estas fechas. Se suman en cuanto alguien marque entrada y salida.</p>}
          <ul className="space-y-2">
            {h.d.filas.map((r) => (
              <li key={`${r.fecha}-${r.id_usuario}`} className="rounded-xl border border-gray-200 bg-white p-3 text-sm">
                <div className="font-semibold">{r.nombre} · {r.fecha}</div>
                <div>{r.horas.toFixed(2)} h{r.comida_min != null ? ` · comida ${r.comida_min} de ${r.comida_permitida} min` : ''}{r.sin_salida ? ' · Sin salida' : ''}</div>
              </li>
            ))}
          </ul>
          <p className="text-sm text-gray-600">{h.d.aviso}</p>
        </div>
      )}
    </Estado>
  );
}

function Horarios() {
  const h = useDatos('/asistencia/horarios');
  // Lo editado se guarda junto con la respuesta de la que salió: al
  // recargar llega otra respuesta y la copia se rehace sola, sin efecto.
  const [edicion, setEdicion] = useState(null);
  const [msg, setMsg] = useState(null);
  const dias = edicion && edicion.base === h.d ? edicion.lista
    : (h.d ? h.d.general.dias.map((x) => ({ ...x })) : null);
  const cambiar = (i, campo, v) => setEdicion({ base: h.d, lista: dias.map((x, j) => (j === i ? { ...x, [campo]: v } : x)) });
  const malo = (dias || []).find((x) => x.laboral && (!x.entrada || !x.salida || x.salida <= x.entrada));
  async function guardar() {
    try {
      const r = await api.put('/asistencia/horarios/general', { dias: dias.map((x) => ({
        laboral: x.laboral, entrada: x.laboral ? x.entrada : null, salida: x.laboral ? x.salida : null,
        comida_min: Number(x.comida_min), tolerancia_min: Number(x.tolerancia_min) })) });
      setMsg(r.mensaje); h.recargar();
    } catch (e) { setMsg(`${e.message} ${e.queHacer || ''}`); }
  }
  return (
    <Estado {...h} que="los horarios">
      {dias && (
        <div className="space-y-3">
          <p className="text-sm text-gray-700">Horario general de la empresa{h.d.general.por_omision ? ' (por omisión: lunes a viernes de 8:00 a 18:00)' : ''}. Los cambios valen desde hoy; los días pasados se quedan con el horario que tenían.</p>
          <Ayuda id="terminal.asistencia.tolerancia" />
          {dias.map((x, i) => (
            <fieldset key={x.dia_semana} className="space-y-2 rounded-xl border border-gray-200 bg-white p-3">
              <legend className="font-semibold">{DIAS[i]}</legend>
              <label className="flex min-h-10 items-center gap-2 text-sm">
                <input type="checkbox" checked={x.laboral} onChange={(e) => cambiar(i, 'laboral', e.target.checked)} className="h-5 w-5" /> Se trabaja
              </label>
              {x.laboral && (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <label>Entrada<input type="time" value={x.entrada || ''} onChange={(e) => cambiar(i, 'entrada', e.target.value)} className="mt-1 min-h-12 w-full rounded-lg border px-2" /></label>
                  <label>Salida<input type="time" value={x.salida || ''} onChange={(e) => cambiar(i, 'salida', e.target.value)} className="mt-1 min-h-12 w-full rounded-lg border px-2" /></label>
                  <label>Comida (min)<input inputMode="numeric" value={x.comida_min} onChange={(e) => cambiar(i, 'comida_min', e.target.value.replace(/\D/g, ''))} className="mt-1 min-h-12 w-full rounded-lg border px-2" /></label>
                  <label>Margen (min)<input inputMode="numeric" value={x.tolerancia_min} onChange={(e) => cambiar(i, 'tolerancia_min', e.target.value.replace(/\D/g, ''))} className="mt-1 min-h-12 w-full rounded-lg border px-2" /></label>
                </div>
              )}
            </fieldset>
          ))}
          <button type="button" disabled={!!malo} onClick={guardar}
            className="min-h-12 w-full rounded-lg bg-blue-600 font-semibold text-white disabled:bg-gray-300 disabled:text-gray-600">Guardar horario</button>
          {malo && <p className="text-sm text-gray-600">En {DIAS[malo.dia_semana - 1]} la salida tiene que ser después de la entrada.</p>}
          {msg && <p role="status" className="text-sm">{msg}</p>}
        </div>
      )}
    </Estado>
  );
}

export default function Asistencia() {
  const { puede = () => false } = useAuth() || {};
  const [pestana, setPestana] = useState('hoy');
  const visibles = PESTANAS.filter((p) => !p.permiso || puede(p.permiso));
  // Las pestañas son subpantallas de «Hoy»: atrás vuelve a Hoy y cambiar de pestaña sustituye la entrada.
  useSubpantalla('pestana', pestana !== 'hoy', () => setPestana('hoy'), { valor: pestana, abrir: setPestana, reemplaza: true });
  return (
    <div className="mx-auto w-full max-w-3xl space-y-4" data-terminal="asistencia">
      <header className="space-y-1">
        <h1 className="text-xl font-bold text-gray-900">Asistencia</h1>
        <p className="text-sm text-gray-600">Quién llegó, quién está y cuántas horas lleva cada persona.</p>
      </header>
      <Ayuda id="terminal.asistencia.pantalla" />
      <div role="tablist" className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {visibles.map((p) => (
          <button key={p.id} type="button" role="tab" aria-selected={pestana === p.id} onClick={() => setPestana(p.id)}
            className={`flex min-h-12 items-center justify-center gap-2 rounded-lg border font-semibold ${pestana === p.id ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-300 bg-white text-gray-700'}`}>
            <p.icono className="h-4 w-4" aria-hidden="true" /> {p.titulo}
          </button>
        ))}
      </div>
      {pestana === 'hoy' && <Hoy />}
      {pestana === 'semana' && <Semana />}
      {pestana === 'horas' && <Horas />}
      {pestana === 'horarios' && <Horarios />}
      {pestana === 'revision' && <Revision />}
      {pestana === 'avisos' && <Avisos />}
      {pestana === 'rostro' && <Rostro puede={puede} />}
      {pestana === 'mios' && <MisRegistros />}
      <p className="text-xs text-gray-500">{NO_NOMINA}</p>
      <Ayuda id="terminal.asistencia.ficha" />
    </div>
  );
}
